import React, { useEffect } from 'react';
import {
  Channel,
  Envs,
  PageComponentOptions,
  PageComponentType,
  PageType,
  RouterHistoryEntry,
  checkAllowedChannel,
  useAppContext,
  useCurrentMenu,
  useEnvContext,
  usePageAttributesStore,
  useRouterHistory,
  useWebviewContext,
  withAuth,
} from 'scbk-services';
import { useAdobeDataLayer, useNavBar, usePopupStore, useProgramInstall } from 'scbk-shared-module';
import { PhoneAuthProvider } from 'scbk-shared-module/modules/Identity/Provider/RemoteIdvProvider';
import { DelfinoG3Component } from 'scbk-shared-module/modules/ProgramInstall';
import { NFilterPublicKeyProvider } from 'scbk-shared-module/modules/SecureKeypad/providers/NFilterPublicKeyProvider';
import { withCustInfoLeaked } from './withCustInfoLeaked';
import { withPageLoadCompleted } from './withPageLoadCompleted';

/**
 * withPageComponent HOC Option
 */
export interface WithPageComponentOptions<T> extends PageComponentOptions {
  defaultRouteData?: T;
  showNavbar?: boolean;
  forceCheckCode?: string;
  preload?: boolean;
}

const trackedPageView = new Set<string>();
const installedPageView = new Set<string>();

/**
 * 업무 화면 컴포넌트 HOC
 * @param options
 * @param Component
 * @returns
 */
export const withPageComponent = <P extends object = {}, T extends object = {}>(
  id: string,
  options: WithPageComponentOptions<T>,
  Component: PageComponentType<P & { routeData: T }>,
): PageComponentType<P> => {
  //***************************
  //화면유형 획득
  //***************************
  let pageType = options.type;
  if (!pageType) {
    pageType = getPageTypeFromPageId(id);
  }

  const displayName = Component.displayName || Component.name || 'Component';

  //***************************
  //메인화면 타입인 경우, 권한/허용가능채널 체크 처리를 한다.
  //***************************
  if (pageType === PageType.MAIN_SCREEN) {
    const titleProps = options?.layoutOptions?.titleProps as Record<string, unknown> | undefined;
    const closeProps = options?.layoutOptions?.closeProps as Record<string, unknown> | undefined;
    //const preload = options.preload ?? false;
    //const forceCheckCode = options.forceCheckCode ?? '';

    //권한체크 옵션
    const withAuthOptions = {
      id,
      ...options,
      ...getTitleProps(titleProps, ''), //TODO: cn값 확정 해야됨 (titleProps 기본값 추가)
      ...getCloseProps(closeProps, ''), //TODO: cn값 확정 해야됨 (closeProps 기본값 추가)
    };

    //***************************
    //이용시간체크 및 권한체크 수행
    //***************************

    const WrappedComponent = withCustInfoLeaked(withAuth({ ...withAuthOptions }, Component));

    //***************************
    //페이지 템플릿 적용
    //***************************

    /* PageOption.template 가 존재할경우 해당 템플릿 적용 아니면, DefaultLayout 적용 */
    const Wrapper: React.FC<P & { routeData: T }> = (props: P) => {
      const { getEnv } = useEnvContext();
      const { stack, currentRoute } = useRouterHistory.getState();
      const { getContext } = useAppContext();
      const { getMenuField } = useCurrentMenu();
      const { push } = useAdobeDataLayer();
      const programInstall = useProgramInstall();
      const { isIpinsideReady, check: installCheck } = programInstall;
      const entry = usePopupStore((state) => state.entry);
      const { status: webviewStatus } = useWebviewContext();
      const { navBarControl } = useNavBar();
      const currentChannel = getContext().channel as Channel;
      const hasAllowed = checkAllowedChannel(currentChannel, options.allowedChannels);

      if (!hasAllowed) {
        return <>{'지원하지 않는 채널에서 화면 호출 함... 처리 필요'}</>;
      }

      const routeData = getRouteData(id, stack, options.defaultRouteData ?? {}) as T;
      const showNavbar = options.showNavbar ?? false;

      //********************************************************
      //페이지 속성정보
      //********************************************************
      const pageAttributes = options.pageAttributes ?? {};
      const setPageAttributes = usePageAttributesStore((state) => state.set);
      setPageAttributes(id, { ...pageAttributes });

      /**
       * 페이지로드 완료 처리 및 하단 탭바 이벤트 처리
       */
      useEffect(() => {
        // 모바일뱅킹이 아닌경우
        if (currentChannel !== Channel.MB) {
          return;
        }

        console.log(`# withPageComponent showNavbar=${showNavbar}`);

        requestAnimationFrame(() => {
          setTimeout(() => {
            navBarControl(showNavbar);
          }, 100);
        });
      }, [currentChannel, showNavbar, entry]);

      /**
       * Adobe Analytics 실행 (모든 Route 실행시)
       */
      useEffect(() => {
        if (trackedPageView.has(id)) return;
        trackedPageView.add(id);

        requestAnimationFrame(() => {
          setTimeout(() => {
            push({
              event: 'page_view',
              screen_name: options?.layoutOptions?.description || '',
              includeUserInfo: true,
            });
          }, 0);
        });

        setTimeout(() => {
          trackedPageView.delete(id);
        }, 100);
      }, [id]);

      /**
       * IB 솔루션 설치체크
       */
      useEffect(() => {
        if (currentChannel !== Channel.IB) return;
        if (!isIpinsideReady || installedPageView.has(id)) return;
        installedPageView.add(id);

        if (currentRoute?.routeType !== 'menu') return;

        const securityLevel = getMenuField('securityLevel');

        if (securityLevel) {
          installCheck(securityLevel);
        }

        setTimeout(() => {
          installedPageView.delete(id);
        }, 100);
      }, [id, isIpinsideReady]);

      useEffect(() => {
        console.log('### withPageComponent MOUNT');
        // 2026.06.23 권태민 hoc 로드 완료시 scroll 제일 위로 이동
        window.scrollTo(0, 0);
        console.log('document.activeElement : ', document.activeElement);
        return () => {
          console.log('### withPageComponent UNMOUNT');
          // 2026.09.02 권태민 hoc 로드 완료 시 데스크탑으로 focus 초기화
          // if (currentChannel !== Channel.IB) return;

          // const skipLink = document.getElementsByClassName('skip-item-link')[0] as HTMLElement;
          // if (skipLink) {
          //   skipLink?.focus();
          // }
        };
      }, []);

      useEffect(() => {
        console.log(`### CSD page ${id} webview status=${webviewStatus}`);
      }, [webviewStatus]);

      return (
        <>
          {getEnv() === Envs.LOCAL || getEnv() === Envs.MOCK ? (
            <>
              <PhoneAuthProvider>
                <WrappedComponent {...props} routeData={routeData} />
              </PhoneAuthProvider>
            </>
          ) : (
            <NFilterPublicKeyProvider>
              <PhoneAuthProvider>
                <WrappedComponent {...props} routeData={routeData} />
              </PhoneAuthProvider>
            </NFilterPublicKeyProvider>
          )}
          {currentChannel === Channel.IB && <DelfinoG3Component {...programInstall} />}
        </>
      );
    };

    //**************************************
    // 페이지 로드 완료 HOC 적용
    //**************************************
    const PageWrapper = withPageLoadCompleted(Wrapper);
    //const PageWrapper = Wrapper;

    (PageWrapper as PageComponentType<P>).displayName = `withPageComponent(${displayName})`;
    (PageWrapper as PageComponentType<P>).id = id;
    (PageWrapper as PageComponentType<P>).options = options;

    return PageWrapper as PageComponentType<P>;
  } else {
    //팝업/바텀시트/탭 등등
    const PageWrapper: React.FC<P> = (props: P) => {
      const routeData: T = {} as T;
      return <Component {...props} routeData={routeData} />;
    };

    (PageWrapper as PageComponentType<P>).displayName = `withPageComponent(${displayName})`;
    (PageWrapper as PageComponentType<P>).id = id;
    (PageWrapper as PageComponentType<P>).options = options;
    return PageWrapper as PageComponentType<P>;
  }
};

/**
 * 화면ID에서 화면유형을 획득한다.
 * @param id 화면ID
 * @returns 화면ID에서 획득한 화면유형
 */
const getPageTypeFromPageId = (id: string): PageType => {
  if (id.endsWith('MS')) return PageType.MAIN_SCREEN;
  if (id.endsWith('TB')) return PageType.TAB;
  if (id.endsWith('FP')) return PageType.FULL_POPUP;
  if (id.endsWith('DR')) return PageType.DRAWER;
  if (id.endsWith('DG')) return PageType.DIALOG;
  return PageType.NONE;
};

/**
 * 화면 route data를 가져온다.
 * @param id 화면ID
 * @param routeStack 라우터 스택
 * @param defaultRouteData 화면컴포넌트에서 옵션으로 설정한 routeData 설정값
 * @returns
 */
const getRouteData = (
  id: string,
  routeStack: RouterHistoryEntry[],
  defaultRouteData: any = {},
): any => {
  const routeEntry = [...routeStack].reverse().find((v) => v.id === id);
  let routeData = { ...routeEntry?.routeData };
  if (!routeData) {
    routeData = { ...defaultRouteData };
  }
  return routeData;
};

const getTitleProps = (props?: Record<string, unknown>, cn?: string): any => {
  const safeProps = props ?? {};

  const defaultTitleProps = {
    'data-cn': cn,
    'data-ac': '_page_content_',
    'data-tx': '_page_title_',
  };

  return {
    titleProps: {
      ...defaultTitleProps,
      ...safeProps,
    },
  };
};

const getCloseProps = (props?: Record<string, unknown>, cn?: string): any => {
  const safeProps = props ?? {};
  const defaultCloseProps = {
    'data-cn': cn,
    'data-ac': '_page_content_',
    'data-tx': '_close_icon_',
  };

  return {
    closeProps: {
      ...defaultCloseProps,
      ...safeProps,
    },
  };
};
